import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, Clock, ImagePlus, MapPin, Trash2, Users } from 'lucide-react';
import { UserContext } from '../App';
import GatherFormPage from '../components/common/GatherFormPage';
import { useModal } from '../context/ModalContext';
import { supabase } from '../utils/supabaseClient';
import { awardPoints } from '../utils/pointsService';
import { EVENT_CATEGORIES, SPORT_CATEGORY, SPORT_SUBCATEGORIES } from '../utils/eventCategories';
import { EVENT_CITIES, TAIWAN_CITIES } from '../utils/taiwanCities';
import { deleteEventImage, MAX_EVENT_IMAGE_SIZE, uploadEventImage } from '../utils/eventImages';

const EMPTY_FORM = {
  title: '',
  date: '',
  startTime: '',
  endTime: '',
  city: '',
  location: '',
  address: '',
  category: '',
  subcategory: '',
  max_participants: '',
  description: ''
};

const inputStyle = {
  boxSizing: 'border-box',
  width: '100%',
  minHeight: '48px',
  padding: '12px 14px',
  color: '#334155',
  background: '#f8fafc',
  border: '1px solid #dbe4ee',
  borderRadius: '14px',
  fontFamily: 'inherit',
  fontSize: '16px'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  color: '#334155',
  fontSize: '13px',
  fontWeight: '750'
};

const toDateInputValue = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateRange = maxMonthsAhead => {
  const today = new Date();
  const max = new Date(today);
  max.setMonth(max.getMonth() + maxMonthsAhead);
  return { minDate: toDateInputValue(today), maxDate: toDateInputValue(max) };
};

const getInitialCreateForm = (draftKey, isSport) => {
  try {
    const draft = window.localStorage.getItem(draftKey);
    const parsed = draft ? JSON.parse(draft) : {};
    return {
      ...EMPTY_FORM,
      ...parsed,
      category: isSport ? SPORT_CATEGORY : (parsed.category || '')
    };
  } catch {
    return { ...EMPTY_FORM, category: isSport ? SPORT_CATEGORY : '' };
  }
};

function EventPreview({ form, imagePreview, isSport }) {
  return (
    <article style={{ overflow: 'hidden', background: '#ffffff', border: '1px solid #dbe4ee', borderRadius: '18px', boxShadow: '0 8px 24px rgba(15,23,42,0.07)' }}>
      {imagePreview ? (
        <img src={imagePreview} alt="活動封面預覽" style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover' }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '112px', color: '#4e8bca', background: 'linear-gradient(135deg, #eef4f9 0%, #dbe9f5 100%)' }}>
          <Calendar size={40} strokeWidth={1.5} />
        </div>
      )}
      <div style={{ padding: '17px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
          <span style={{ padding: '4px 9px', color: '#376ba1', background: '#eef4f9', borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>
            {(isSport ? form.subcategory : form.category) || (isSport ? '運動項目' : '活動類別')}
          </span>
          {form.city && <span style={{ padding: '4px 9px', color: '#475569', background: '#f1f5f9', borderRadius: '999px', fontSize: '11px', fontWeight: '800' }}>{form.city}</span>}
        </div>
        <h3 style={{ margin: '0 0 9px', color: '#0f172a', fontSize: '19px', fontWeight: '850', lineHeight: 1.4 }}>{form.title.trim() || (isSport ? '你的運動揪團標題' : '你的活動標題')}</h3>
        <p style={{ margin: '0 0 14px', color: '#64748b', fontSize: '12px', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{form.description.trim() || '補充活動內容、參加條件與注意事項，讓大家快速了解這場聚會。'}</p>
        <div style={{ display: 'grid', gap: '8px', paddingTop: '13px', borderTop: '1px solid #eef2f6' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#475569', fontSize: '12px' }}><Clock size={15} color="#4e8bca" />{form.date || '尚未選擇日期'}　{form.startTime || '--:--'}－{form.endTime || '--:--'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#475569', fontSize: '12px' }}><MapPin size={15} color="#4e8bca" />{form.location || '尚未填寫地點'}{form.address ? ` · ${form.address}` : ''}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '7px', color: '#475569', fontSize: '12px' }}><Users size={15} color="#4e8bca" />最多{form.max_participants || '—'}人</span>
        </div>
      </div>
    </article>
  );
}

export default function CreateEvent({ mode = 'event' }) {
  const isSport = mode === 'sport';
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const { showAlert } = useModal();
  const currentUserId = currentUser?._id || currentUser?.id || currentUser?.auth0Id;
  const maxMonthsAhead = isSport ? 2 : 6;
  const draftKey = `joinu_create_${isSport ? 'sport' : 'event'}_draft`;
  const [form, setForm] = useState(() => getInitialCreateForm(draftKey, isSport));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [errors, setErrors] = useState({});
  const { minDate, maxDate } = useMemo(() => getDateRange(maxMonthsAhead), [maxMonthsAhead]);
  const categories = EVENT_CATEGORIES;
  const cities = isSport ? TAIWAN_CITIES : EVENT_CITIES;

  useEffect(() => {
    if (!isEditing || !currentUserId) return undefined;
    let isMounted = true;

    const loadEvent = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase.from('events').select('*').eq('id', id).single();
        if (error) throw error;
        if (data.organizer_id !== currentUserId) {
          showAlert('提示', '你沒有權限編輯這個揪團');
          navigate(`/events/${id}`, { replace: true });
          return;
        }
        if (data.status === 'cancelled') {
          showAlert('提示', '已取消的活動只能查看歷史紀錄，無法再編輯');
          navigate(`/events/${id}`, { replace: true });
          return;
        }
        if (isMounted) {
          setForm({
            title: data.title || '',
            date: data.date || '',
            startTime: data.start_time || '',
            endTime: data.end_time || '',
            city: data.city || '',
            location: data.location || '',
            address: data.address || '',
            category: isSport ? SPORT_CATEGORY : (data.category || ''),
            subcategory: data.subcategory || '',
            max_participants: data.max_participants || '',
            description: data.description || ''
          });
          setImagePreview(data.image_url || '');
          setOriginalImageUrl(data.image_url || '');
          setIsDirty(false);
        }
      } catch (error) {
        console.error('載入活動編輯資料失敗:', error);
        showAlert('錯誤', '載入活動資料失敗，請稍後再試');
        navigate(`/events/${id}`, { replace: true });
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadEvent();
    return () => { isMounted = false; };
  }, [currentUserId, id, isEditing, isSport, navigate, showAlert]);

  useEffect(() => {
    if (isEditing) return;
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(draftKey, JSON.stringify(form));
        setDraftSavedAt(new Date());
      } catch {
        // Storage may be unavailable in private browsing; the form remains usable.
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [draftKey, form, isEditing]);

  useEffect(() => {
    if (!isEditing || !isDirty) return undefined;
    const warnBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isDirty, isEditing]);

  useEffect(() => () => {
    if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const updateField = (field, value) => {
    setForm(previous => ({ ...previous, [field]: value }));
    setErrors(previous => ({ ...previous, [field]: undefined }));
    setIsDirty(true);
  };

  const showErrors = nextErrors => {
    setErrors(nextErrors);
    const firstField = Object.keys(nextErrors)[0];
    window.requestAnimationFrame(() => {
      const field = document.getElementById(`event-form-${firstField}`);
      field?.focus();
      field?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!form.title.trim()) nextErrors.title = '請填寫活動標題';
    if (!form.date) nextErrors.date = '請選擇活動日期';
    if (form.date && (form.date < minDate || form.date > maxDate)) nextErrors.date = `日期需在今天起${maxMonthsAhead}個月內`;
    if (!form.startTime) nextErrors.startTime = '請選擇開始時間';
    if (!form.endTime) nextErrors.endTime = '請選擇結束時間';
    if (form.startTime && form.endTime && form.endTime <= form.startTime) nextErrors.endTime = '結束時間必須晚於開始時間';
    if (!form.city) nextErrors.city = '請選擇縣市';
    if (!form.location.trim()) nextErrors.location = '請填寫活動地點';
    if (!form.address.trim()) nextErrors.address = '請填寫詳細地址';
    if (!isSport && !form.category) nextErrors.category = '請選擇活動類別';
    if (isSport && !form.subcategory) nextErrors.subcategory = '請選擇運動項目';
    if (!form.max_participants || Number(form.max_participants) < 1) nextErrors.max_participants = '請設定至少1人的人數上限';
    if (!form.description.trim()) nextErrors.description = '請填寫活動描述';
    if (Object.keys(nextErrors).length > 0) {
      showErrors(nextErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleImageChange = event => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showAlert('提示', '請選擇 JPG、PNG 或 WebP 圖片');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_EVENT_IMAGE_SIZE) {
      showAlert('提示', '活動封面需小於 8MB');
      event.target.value = '';
      return;
    }
    if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setIsDirty(true);
  };

  const removeImage = () => {
    if (imagePreview.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview('');
    setIsDirty(true);
  };

  const handleBack = () => {
    if (isEditing && isDirty && !window.confirm('尚有未儲存的變更，確定要離開嗎？')) return;
    navigate(isEditing ? `/events/${id}` : (isSport ? '/sports' : '/events'));
  };

  const handleSaveDraft = () => {
    try {
      window.localStorage.setItem(draftKey, JSON.stringify(form));
    } catch {
      // If storage is unavailable, leaving still follows the user's explicit action.
    }
    navigate(isSport ? '/sports' : '/events');
  };

  const eventPayload = imageUrl => ({
    title: form.title.trim(),
    description: form.description.trim(),
    date: form.date,
    start_time: form.startTime,
    end_time: form.endTime,
    location: form.location.trim(),
    address: form.address.trim(),
    city: form.city,
    category: isSport ? SPORT_CATEGORY : form.category,
    subcategory: isSport ? form.subcategory : null,
    max_participants: Number(form.max_participants),
    image_url: imageUrl
  });

  const handleSubmit = async () => {
    if (!validateForm() || !currentUserId) return;
    setIsSaving(true);
    let uploadedImageUrl = null;
    try {
      if (isEditing) {
        const { data: participantRows, error: countError } = await supabase
          .from('event_participants')
          .select('user_id, status')
          .eq('event_id', id);
        if (countError) throw countError;
        const currentCount = participantRows?.length ?? 0;
        if (currentCount > 0 && Number(form.max_participants) < currentCount) {
          showErrors({ max_participants: `目前已有${currentCount}人報名，人數上限不能少於${currentCount}人` });
          return;
        }

        if (imageFile) uploadedImageUrl = await uploadEventImage(currentUserId, imageFile);
        const nextImageUrl = uploadedImageUrl || imagePreview || null;
        const { data: updatedEvents, error } = await supabase
          .from('events')
          .update(eventPayload(nextImageUrl))
          .eq('id', id)
          .select('id');
        if (error) throw error;
        if (!updatedEvents?.length) throw new Error('更新失敗，找不到目標資料');

        if (originalImageUrl && originalImageUrl !== nextImageUrl) await deleteEventImage(originalImageUrl);
        setIsDirty(false);
        showAlert('成功', isSport ? '運動揪團更新成功！' : '活動更新成功！');
        navigate(`/events/${id}`, { replace: true });
      } else {
        if (imageFile) uploadedImageUrl = await uploadEventImage(currentUserId, imageFile);
        const { data: newEvent, error } = await supabase
          .from('events')
          .insert({ ...eventPayload(uploadedImageUrl), organizer_id: currentUserId })
          .select('id')
          .single();
        if (error) throw error;
        window.localStorage.removeItem(draftKey);
        awardPoints('create_event', newEvent.id);
        showAlert('成功', isSport ? '運動揪團建立成功！' : '活動建立成功！');
        navigate(isSport ? '/sports' : '/events', { replace: true });
      }
    } catch (error) {
      if (uploadedImageUrl) await deleteEventImage(uploadedImageUrl);
      console.error(isEditing ? '更新活動錯誤:' : '建立活動錯誤:', error);
      showAlert('錯誤', `${isEditing ? '更新' : '建立'}失敗：${error?.message || '請稍後再試'}`);
    } finally {
      setIsSaving(false);
    }
  };

  const ErrorText = ({ field }) => errors[field] ? <p role="alert" style={{ margin: '6px 0 0', color: '#dc2626', fontSize: '12px', fontWeight: '650' }}>{errors[field]}</p> : null;
  const errorBorder = field => errors[field] ? '#ef4444' : '#dbe4ee';

  return (
    <GatherFormPage
      title={isEditing ? (isSport ? '編輯運動揪團' : '編輯活動') : (isSport ? '發起運動揪團' : '發起活動')}
      subtitle={isEditing ? '更新活動資訊與參加條件' : '一次填完，隨時查看發布預覽'}
      backLabel={`返回${isEditing ? '活動詳情' : (isSport ? '揪運動列表' : '揪活動列表')}`}
      onBack={handleBack}
      preview={<EventPreview form={form} imagePreview={imagePreview} isSport={isSport} />}
      previewTitle={isEditing ? '變更預覽' : '發布預覽'}
      primaryLabel={isEditing ? '儲存變更' : (isSport ? '發布運動揪團' : '發布活動')}
      savingLabel={isEditing ? '儲存中...' : '發布中...'}
      onPrimary={handleSubmit}
      draftStatus={!isEditing ? `文字草稿已儲存於此裝置${draftSavedAt ? ` · ${draftSavedAt.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}` : ''}` : ''}
      onSaveDraft={!isEditing ? handleSaveDraft : null}
      isSaving={isSaving}
      isLoading={isLoading}
    >
      <section className="gather-form-section" aria-labelledby="event-basic-heading">
        <div className="gather-form-section-header">
          <h2 id="event-basic-heading">基本資訊</h2>
          <p>先用清楚的標題，讓大家快速理解這場揪團。</p>
        </div>
        <div className="gather-form-fields">
          <div><label htmlFor="event-form-title" style={labelStyle}>活動標題 *</label><input id="event-form-title" value={form.title} onChange={event => updateField('title', event.target.value)} placeholder={isSport ? '例：週六台北羽球新手團' : '例：週末桌遊交流聚會'} style={{ ...inputStyle, borderColor: errorBorder('title') }} /><ErrorText field="title" /></div>
          <div className="gather-form-fields-2">
            {isSport ? <div><label htmlFor="event-form-subcategory" style={labelStyle}>運動項目 *</label><select id="event-form-subcategory" value={form.subcategory} onChange={event => updateField('subcategory', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('subcategory') }}><option value="">選擇運動項目</option>{SPORT_SUBCATEGORIES.map(item => <option key={item} value={item}>{item}</option>)}</select><ErrorText field="subcategory" /></div> : <div><label htmlFor="event-form-category" style={labelStyle}>活動類別 *</label><select id="event-form-category" value={form.category} onChange={event => updateField('category', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('category') }}><option value="">選擇活動類別</option>{categories.map(category => <option key={category} value={category}>{category}</option>)}</select><ErrorText field="category" /></div>}
            <div><label htmlFor="event-form-max_participants" style={labelStyle}>人數上限 *</label><input id="event-form-max_participants" type="number" inputMode="numeric" min="1" value={form.max_participants} onChange={event => updateField('max_participants', event.target.value)} placeholder="例：8" style={{ ...inputStyle, borderColor: errorBorder('max_participants') }} /><ErrorText field="max_participants" /></div>
          </div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="event-time-heading">
        <div className="gather-form-section-header">
          <h2 id="event-time-heading">時間與地點</h2>
          <p>讓參加者快速確認活動是否符合自己的行程。</p>
        </div>
        <div className="gather-form-fields">
          <div className="gather-form-fields-2">
            <div><label htmlFor="event-form-date" style={labelStyle}>活動日期 *</label><input id="event-form-date" type="date" min={minDate} max={maxDate} value={form.date} onChange={event => updateField('date', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('date') }} /><ErrorText field="date" /></div>
            <div><label htmlFor="event-form-city" style={labelStyle}>活動縣市 *</label><select id="event-form-city" value={form.city} onChange={event => updateField('city', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('city') }}><option value="">選擇縣市</option>{cities.map(city => <option key={city} value={city}>{city}</option>)}</select><ErrorText field="city" /></div>
          </div>
          <div className="gather-form-fields-2">
            <div><label htmlFor="event-form-startTime" style={labelStyle}>開始時間 *</label><input id="event-form-startTime" type="time" value={form.startTime} onChange={event => updateField('startTime', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('startTime') }} /><ErrorText field="startTime" /></div>
            <div><label htmlFor="event-form-endTime" style={labelStyle}>結束時間 *</label><input id="event-form-endTime" type="time" value={form.endTime} onChange={event => updateField('endTime', event.target.value)} style={{ ...inputStyle, borderColor: errorBorder('endTime') }} /><ErrorText field="endTime" /></div>
          </div>
          <div><label htmlFor="event-form-location" style={labelStyle}>活動地點 *</label><input id="event-form-location" value={form.location} onChange={event => updateField('location', event.target.value)} placeholder="例：大安運動中心、線上 Google Meet" style={{ ...inputStyle, borderColor: errorBorder('location') }} /><ErrorText field="location" /></div>
          <div><label htmlFor="event-form-address" style={labelStyle}>詳細地址／集合方式 *</label><input id="event-form-address" value={form.address} onChange={event => updateField('address', event.target.value)} placeholder="輸入地址、樓層或集合點" style={{ ...inputStyle, borderColor: errorBorder('address') }} /><ErrorText field="address" /></div>
        </div>
      </section>

      <section className="gather-form-section" aria-labelledby="event-detail-heading">
        <div className="gather-form-section-header">
          <h2 id="event-detail-heading">詳細說明與照片</h2>
          <p>清楚交代流程、程度、費用及需要攜帶的物品。</p>
        </div>
        <div className="gather-form-fields">
          <div><label htmlFor="event-form-description" style={labelStyle}>活動描述 *</label><textarea id="event-form-description" rows={7} value={form.description} onChange={event => updateField('description', event.target.value)} placeholder="介紹活動流程、程度、費用、需要攜帶的物品與其他注意事項..." style={{ ...inputStyle, minHeight: '170px', resize: 'vertical', lineHeight: 1.65, borderColor: errorBorder('description') }} /><ErrorText field="description" /></div>
          <div>
            <span style={labelStyle}>活動封面（選填，限一張）</span>
            {imagePreview ? (
              <div style={{ position: 'relative', height: '230px', overflow: 'hidden', background: '#eef4f9', borderRadius: '18px' }}>
                <img src={imagePreview} alt="活動封面預覽" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <button type="button" aria-label="移除活動封面" onClick={removeImage} style={{ position: 'absolute', top: '12px', right: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '44px', height: '44px', padding: 0, color: '#ffffff', background: 'rgba(15,23,42,0.72)', border: 'none', borderRadius: '50%', cursor: 'pointer' }}><Trash2 size={18} /></button>
              </div>
            ) : (
              <label htmlFor="event-form-image" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '170px', padding: '20px', color: '#376ba1', background: '#f6f9fc', border: '1.5px dashed #9cc2e6', borderRadius: '18px', cursor: 'pointer' }}>
                <ImagePlus size={32} />
                <span style={{ marginTop: '10px', fontSize: '14px', fontWeight: '800' }}>上傳活動封面</span>
                <span style={{ marginTop: '4px', color: '#64748b', fontSize: '11px' }}>支援 JPG、PNG、WebP，檔案小於 8MB</span>
              </label>
            )}
            <input id="event-form-image" aria-label="上傳活動封面照片" type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageChange} style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap' }} />
          </div>
        </div>
      </section>
    </GatherFormPage>
  );
}
